import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';
import { logError } from '@edx/frontend-platform/logging';

import type {
  Cohort,
  CohortDetail,
  CohortInfo,
  CohortMember,
  CohortMemberRolePatch,
  CohortMemberProgress,
  CohortThread,
  FacilitatorStatus,
  JoinCohortResponse,
  PaginatedResponse,
} from '../types';
import { apiRequest } from './utils/apiClient';

export type FacilitatorGuideFormat = 'pdf' | 'pptx';

export interface FacilitatorGuideAvailability {
  language: string;
  pdf: boolean;
  pptx: boolean;
}

export interface CreateCohortPayload {
  name: string;
  description?: string;
  language: string;
}

/**
 * Body of the cohort PATCH (`PATCH api/cohorts/v1/cohorts/<slug>/`). Partial —
 * an empty body is a no-op. `slug` (the invite URL) is owner-only; a renamed
 * slug moves the resource, so use the returned `slug` for subsequent requests.
 */
export interface UpdateCohortPayload {
  name?: string;
  description?: string;
  slug?: string;
}

/**
 * URL of the facilitator guide file. Fetch via `downloadFacilitatorGuide` below,
 * not a plain anchor `href` — the cross-origin `download` attribute is ignored,
 * so an error response would navigate away to the raw JSON page.
 */
export const getFacilitatorGuideUrl = (
  language: string,
  format: FacilitatorGuideFormat,
): string => (
  `${getConfig().LMS_BASE_URL}/api/cohorts/v1/facilitator-guide/${language}/${format}/`
);

/**
 * Extracts the file name from a `Content-Disposition: attachment` header,
 * falling back to a constructed guide name when the header is absent/opaque.
 */
const guideFilename = (
  disposition: string,
  language: string,
  format: FacilitatorGuideFormat,
): string => (
  /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? `facilitator-guide-${language}.${format}`
);

/**
 * Downloads the facilitator guide via an authenticated blob fetch plus a
 * programmatic anchor save. Rejects on failure so callers can show an error.
 */
export const downloadFacilitatorGuide = async (
  language: string,
  format: FacilitatorGuideFormat,
): Promise<void> => {
  try {
    const res = await getAuthenticatedHttpClient().get(
      getFacilitatorGuideUrl(language, format),
      { responseType: 'blob', withCredentials: true },
    ) as { data: Blob; headers?: Record<string, string> };

    const blobUrl = URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = guideFilename(res.headers?.['content-disposition'] ?? '', language, format);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    logError('Error downloading facilitator guide', error);
    throw error;
  }
};

const cohortsUrl = (path = ''): string => (
  `${getConfig().LMS_BASE_URL}/api/cohorts/v1/cohorts/${path}`
);

export const cohortsApi = {
  getFacilitatorStatus: async (
    callback: (res: FacilitatorStatus) => void,
    errorCallback?: (error: Error) => void,
  ): Promise<void> => {
    const url = `${getConfig().LMS_BASE_URL}/api/cohorts/v1/facilitators/me/`;

    try {
      const data = await apiRequest<FacilitatorStatus>({
        url,
        isAuth: true,
        method: 'GET',
      });

      callback(data);
    } catch (error) {
      logError('Error fetching facilitator status', error);
      if (errorCallback) {
        errorCallback(error as Error);
      }
    }
  },

  getFacilitatorGuideAvailability: async (
    language: string,
    callback: (res: FacilitatorGuideAvailability) => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    const url = `${getConfig().LMS_BASE_URL}/api/cohorts/v1/facilitator-guide/${language}/`;

    try {
      const data = await apiRequest<FacilitatorGuideAvailability>({
        url,
        isAuth: true,
        method: 'GET',
      });

      callback(data);
    } catch (error) {
      logError('Error fetching facilitator guide availability', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },

  createCohort: async (
    payload: CreateCohortPayload,
    callback: (res: Cohort) => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    try {
      const data = await apiRequest<Cohort>({
        url: cohortsUrl(),
        isAuth: true,
        method: 'POST',
        contentType: 'application/json',
        data: payload,
      });

      callback(data);
    } catch (error) {
      logError('Error creating cohort', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },

  getMyCohorts: async (
    page: number,
    callback: (res: PaginatedResponse<Cohort>) => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    try {
      const data = await apiRequest<PaginatedResponse<Cohort>>({
        url: cohortsUrl(),
        isAuth: true,
        method: 'GET',
        params: { page },
      });

      callback(data);
    } catch (error) {
      logError('Error fetching my cohorts', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },

  getCohortInfo: async (
    slug: string,
    callback: (res: CohortInfo) => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    try {
      const data = await apiRequest<CohortInfo>({
        url: cohortsUrl(`${slug}/info/`),
        isAuth: false,
        method: 'GET',
      });

      callback(data);
    } catch (error) {
      logError('Error fetching cohort info', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },

  joinCohort: async (
    slug: string,
    callback: (res: JoinCohortResponse) => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    try {
      const data = await apiRequest<JoinCohortResponse>({
        url: cohortsUrl(`${slug}/join/`),
        isAuth: true,
        method: 'POST',
      });

      callback(data);
    } catch (error) {
      logError('Error joining cohort', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },

  getCohortDetail: async (
    slug: string,
    callback: (res: CohortDetail) => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    try {
      const data = await apiRequest<CohortDetail>({
        url: cohortsUrl(`${slug}/`),
        isAuth: true,
        method: 'GET',
      });

      callback(data);
    } catch (error) {
      logError('Error fetching cohort detail', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },

  updateCohort: async (
    slug: string,
    payload: UpdateCohortPayload,
    callback: (res: CohortDetail) => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    try {
      const data = await apiRequest<CohortDetail>({
        url: cohortsUrl(`${slug}/`),
        isAuth: true,
        method: 'PATCH',
        contentType: 'application/json',
        data: payload,
      });

      callback(data);
    } catch (error) {
      logError('Error updating cohort', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },

  getCohortMembers: async (
    slug: string,
    params: { page: number },
    callback: (res: PaginatedResponse<CohortMember>) => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    try {
      const data = await apiRequest<PaginatedResponse<CohortMember>>({
        url: cohortsUrl(`${slug}/members/`),
        isAuth: true,
        method: 'GET',
        params: { page: params.page },
      });

      callback(data);
    } catch (error) {
      logError('Error fetching cohort members', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },

  getCohortProgress: async (
    slug: string,
    params: { page: number; search?: string },
    callback: (res: PaginatedResponse<CohortMemberProgress>) => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    const { page, search } = params;

    try {
      const data = await apiRequest<PaginatedResponse<CohortMemberProgress>>({
        url: cohortsUrl(`${slug}/progress/`),
        isAuth: true,
        method: 'GET',
        params: { page, ...(search ? { search } : {}) },
      });

      callback(data);
    } catch (error) {
      logError('Error fetching cohort progress', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },

  getCohortThreads: async (
    slug: string,
    courseId: string,
    page: number,
    callback: (res: PaginatedResponse<CohortThread>) => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    try {
      const data = await apiRequest<PaginatedResponse<CohortThread>>({
        url: cohortsUrl(`${slug}/forum/threads/`),
        isAuth: true,
        method: 'GET',
        params: { course_id: courseId, page },
      });

      callback(data);
    } catch (error) {
      logError('Error fetching cohort forum threads', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },

  updateCohortMemberRole: async (
    slug: string,
    userId: number,
    role: CohortMemberRolePatch['role'],
    callback: (res: CohortMember) => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    try {
      const data = await apiRequest<CohortMember>({
        url: cohortsUrl(`${slug}/members/${userId}/`),
        isAuth: true,
        method: 'PATCH',
        contentType: 'application/json',
        data: { role } as CohortMemberRolePatch,
      });

      callback(data);
    } catch (error) {
      logError('Error updating cohort member role', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },

  removeCohortMember: async (
    slug: string,
    userId: number,
    callback: () => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    try {
      await apiRequest<void>({
        url: cohortsUrl(`${slug}/members/${userId}/`),
        isAuth: true,
        method: 'DELETE',
      });

      callback();
    } catch (error) {
      logError('Error removing cohort member', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },

  deleteCohortThread: async (
    slug: string,
    threadId: string,
    callback: () => void,
    errorCallback?: (error: unknown) => void,
  ): Promise<void> => {
    try {
      await apiRequest<void>({
        url: cohortsUrl(`${slug}/forum/threads/${threadId}/`),
        isAuth: true,
        method: 'DELETE',
      });

      callback();
    } catch (error) {
      logError('Error deleting cohort forum thread', error);
      if (errorCallback) {
        errorCallback(error);
      }
    }
  },
};
